import { Link } from "react-router-dom";
import { ArrowRight, MessageCircle } from "lucide-react";
import { MediaImage } from "../MediaImage";
import { useSiteMedia } from "../../lib/siteMedia";
import { PRODUCTS, quoteHref } from "../../data/homeContent";
import { WHATSAPP_LINK } from "../../lib/constants";
import { trackEvent } from "../../lib/api";
import { SectionHeading } from "./CtaButtons";

const ProductCard = ({ p, media }) => (
  <li className="card card-hover group flex flex-col overflow-hidden" data-testid={`product-card-${p.id}`}>
    <MediaImage media={media} sizes="(min-width: 1024px) 400px, (min-width: 640px) 50vw, calc(100vw - 32px)" className="aspect-[4/3]"
      imgClassName="transition-transform duration-500 group-hover:scale-[1.03]" />
    <div className="flex flex-1 flex-col p-6">
      <p className="text-[12px] font-semibold uppercase tracking-[0.14em] text-gold">{p.material}</p>
      <h3 className="h3 mt-2"><Link to={p.href} className="hover:text-maas" data-testid={`product-link-${p.id}`}>{p.title}</Link></h3>
      <p className="mt-2 text-[15px] leading-relaxed text-slate-600">{p.text}</p>
      <ul className="mt-4 flex flex-wrap gap-2">{p.specs.map((s) => <li key={s} className="chip">{s}</li>)}</ul>
      <div className="mt-auto flex items-center justify-between gap-3 border-t border-slate-100 pt-5 mt-6">
        <Link to={p.href} className="text-[14.5px] font-semibold text-ink hover:text-maas">Meer informatie</Link>
        <Link to={quoteHref(p.onderwerp)} onClick={() => trackEvent("cta_quote_click", { location: `product-${p.id}` })}
          data-testid={`product-quote-${p.id}`} className="inline-flex items-center gap-1.5 text-[14.5px] font-semibold text-maas hover:text-maas-hover">
          Prijsindicatie <ArrowRight className="h-4 w-4" aria-hidden="true" />
        </Link>
      </div>
    </div>
  </li>
);

const AdviceCard = () => (
  <li className="relative flex flex-col justify-between overflow-hidden rounded-2xl bg-ink p-7 text-white sm:p-8" data-testid="product-card-advies">
    <div className="hero-grid-dark absolute inset-0 opacity-60" aria-hidden="true" />
    <div className="relative">
      <p className="text-[12px] font-semibold uppercase tracking-[0.14em] text-[#D9B97A]">Advies</p>
      <h3 className="mt-3 font-display text-[1.5rem] font-bold leading-tight">Twijfelt u tussen kunststof en aluminium?</h3>
      <p className="mt-3 leading-relaxed text-slate-300">Beide materialen hebben hun sterke punten. Wij leggen de verschillen nuchter uit en adviseren wat bij uw woning past.</p>
      <dl className="mt-6 space-y-3 text-[14.5px]">
        {[["Kunststof", "Onderhoudsarm, goed isolerend en prijsbewust."], ["Aluminium", "Slanke profielen en grote glasvlakken."]].map(([k, v]) => (
          <div key={k} className="rounded-xl border border-white/15 bg-white/5 px-4 py-3">
            <dt className="font-semibold text-white">{k}</dt>
            <dd className="mt-0.5 text-slate-300">{v}</dd>
          </div>
        ))}
      </dl>
    </div>
    <div className="relative mt-8 flex flex-col gap-3">
      <Link to="/aluminium-of-kunststof-kozijnen" className="inline-flex items-center gap-2 font-semibold text-white hover:underline underline-offset-4" data-testid="advies-compare-link">
        Bekijk de vergelijking <ArrowRight className="h-4 w-4" aria-hidden="true" />
      </Link>
      <a href={WHATSAPP_LINK} target="_blank" rel="noopener noreferrer" onClick={() => trackEvent("click_whatsapp", { page_type: "advies-card" })}
        className="inline-flex items-center gap-2 text-slate-300 hover:text-white" data-testid="advies-whatsapp-link">
        <MessageCircle className="h-4 w-4 text-[#4ADE80]" aria-hidden="true" /> Stuur een foto, wij denken mee
      </a>
    </div>
  </li>
);

export const ProductChoice = () => {
  const media = useSiteMedia();
  return (
    <section id="producten" aria-labelledby="producten-title" className="section scroll-mt-24" data-testid="home-products">
      <div className="container-tight">
        <SectionHeading id="producten-title" eyebrow="Ons aanbod" title="Nieuwe kozijnen, puien en deuren — inclusief glas en montage"
          lead="U kiest het materiaal en de uitvoering. Wij verzorgen het inmeten, de levering en de montage van uw complete nieuwe kozijnen." />
        <ul className="mt-10 grid gap-6 sm:grid-cols-2 lg:mt-14 lg:grid-cols-3">
          {PRODUCTS.map((p) => <ProductCard key={p.id} p={p} media={media(p.media)} />)}
          <AdviceCard />
        </ul>
      </div>
    </section>
  );
};
